import CloudIcon from '@mui/icons-material/Cloud';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SpeedIcon from '@mui/icons-material/Speed';
import StorageIcon from '@mui/icons-material/Storage';
import { Box, CardContent, Chip, LinearProgress, Stack, Typography, useTheme } from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { BaseWidget } from '@src/App/Components/Widgets/BaseWidget';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

export default function AdminSystemStatusWidget() {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang);

    const systemMetrics = [
        {
            label: 'Server Status',
            value: 'Online',
            percentage: 99,
            color: 'success.main',
            icon: <CloudIcon />,
        },
        {
            label: 'Datenbank',
            value: 'Optimal',
            percentage: 95,
            color: 'info.main',
            icon: <StorageIcon />,
        },
        {
            label: 'Performance',
            value: 'Gut',
            percentage: 87,
            color: 'warning.main',
            icon: <SpeedIcon />,
        },
    ];

    return (
        <BaseWidget>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    height: { xs: 50, sm: 60, md: 65 },
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.primary.dark}20 100%)`,
                    backdropFilter: 'blur(10px)',
                }}
            >
                <MonitorHeartIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, mr: 1 }}>
                    System Status
                </Typography>
                <Chip label="Live" size="small" color="success" variant="outlined" />
            </Box>

            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: { xs: 2, sm: 2 } }}>
                {/* System Metrics */}
                <Stack spacing={3}>
                    {systemMetrics.map((metric, index) => (
                        <Box key={index}>
                            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                                <Box
                                    sx={{
                                        p: 1,
                                        borderRadius: 2,
                                        background: `linear-gradient(135deg, ${metric.color}20 0%, ${metric.color}10 100%)`,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {React.cloneElement(metric.icon, {
                                        sx: { fontSize: 20, color: metric.color },
                                    })}
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {metric.label}
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {metric.value}
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: metric.color }}>
                                    {metric.percentage}%
                                </Typography>
                            </Stack>
                            <LinearProgress
                                variant="determinate"
                                value={metric.percentage}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: theme.palette.grey[200],
                                    '& .MuiLinearProgress-bar': {
                                        bgcolor: metric.color,
                                        borderRadius: 3,
                                        background: `linear-gradient(90deg, ${metric.color} 0%, ${metric.color}80 100%)`,
                                    },
                                }}
                            />
                        </Box>
                    ))}
                </Stack>

                {/* Overall Status */}
                <Box
                    sx={{
                        mt: 3,
                        p: 2,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.success.main}10 0%, ${theme.palette.success.main}05 100%)`,
                        border: `1px solid ${theme.palette.success.main}20`,
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Gesamtstatus
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                        Alle Systeme funktionsfähig
                    </Typography>
                </Box>
            </CardContent>
        </BaseWidget>
    );
}
